import React, { useState } from 'react';
import PlaybookStatusBadge from './PlaybookStatusBadge';
import type { TransitionLogEntry } from '../types/playbook.types';

interface PlaybookTimelineEntryProps {
  entry: TransitionLogEntry;
}

const TRIGGER_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  user: { bg: 'bg-lex-accent-soft', text: 'text-lex-accent', label: 'Manual' },
  event: { bg: 'bg-lex-accent-soft', text: 'text-lex-accent', label: 'Event' },
  deadline: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Deadline' },
  auto: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: 'Auto' },
  admin_override: { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-700 dark:text-rose-300', label: 'Admin Override' },
};

const PlaybookTimelineEntry: React.FC<PlaybookTimelineEntryProps> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);

  const trigger = TRIGGER_BADGE[entry.triggered_by] || TRIGGER_BADGE.auto;
  const userName = entry.triggeredByUser
    ? `${entry.triggeredByUser.first_name || ''} ${entry.triggeredByUser.last_name || ''}`.trim() || entry.triggeredByUser.email
    : null;
  const timestamp = new Date(entry.executed_at).toLocaleString();

  const hasDetails =
    entry.guards_evaluated || entry.validators_evaluated || entry.actions_executed || entry.form_data;

  return (
    <div className="relative pl-8">
      {/* Timeline dot */}
      <div className="absolute left-0 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white dark:bg-gray-800 ring-2 ring-gray-200 dark:ring-gray-700">
        <span className={`h-2 w-2 rounded-full ${entry.is_forced ? 'bg-rose-500' : 'bg-lex-accent'}`} />
      </div>

      <div
        className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 ${
          hasDetails ? 'cursor-pointer hover:border-gray-300 dark:hover:border-gray-600' : ''
        }`}
        onClick={hasDetails ? () => setExpanded((v) => !v) : undefined}
      >
        {/* Header row */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-gray-900 dark:text-white">
            {entry.transition?.name || 'Status Change'}
          </span>

          {entry.fromStatus && (
            <>
              <PlaybookStatusBadge
                category={entry.fromStatus.category}
                name={entry.fromStatus.name}
              />
              <span className="text-gray-400">&rarr;</span>
            </>
          )}
          <PlaybookStatusBadge
            category={entry.toStatus.category}
            name={entry.toStatus.name}
          />

          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${trigger.bg} ${trigger.text}`}>
            {trigger.label}
          </span>

          {entry.is_forced && (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
              Forced
            </span>
          )}
        </div>

        {/* Meta row */}
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {userName && <span>by {userName}</span>}
          <span>{timestamp}</span>
          {hasDetails && (
            <span className="text-lex-accent">
              {expanded ? 'Hide details' : 'Show details'}
            </span>
          )}
        </div>

        {/* Override reason */}
        {entry.is_forced && entry.override_reason && (
          <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
            <span className="font-medium">Override reason:</span> {entry.override_reason}
            {entry.forcedByUser && (
              <span className="ml-1 text-rose-500">
                (by {entry.forcedByUser.first_name} {entry.forcedByUser.last_name})
              </span>
            )}
          </div>
        )}

        {/* Expanded details */}
        {expanded && (
          <div
            className="mt-3 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3"
            style={{ animation: 'coverageDrillDown 250ms ease-out' }}
          >
            {/* Guards */}
            {entry.guards_evaluated && renderEvaluations('Guards', entry.guards_evaluated)}

            {/* Validators */}
            {entry.validators_evaluated && renderEvaluations('Validators', entry.validators_evaluated)}

            {/* Actions */}
            {entry.actions_executed && (
              <div>
                <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Actions Executed
                </h5>
                <div className="space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                  {Array.isArray(entry.actions_executed)
                    ? (entry.actions_executed as Array<Record<string, unknown>>).map((a, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <span className={`h-1 w-1 rounded-full ${a.success ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                          <span>{String(a.type || a.action_type || 'action')}</span>
                          {a.error ? <span className="text-rose-500">({String(a.error)})</span> : null}
                        </div>
                      ))
                    : <pre className="text-[10px] bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto">
                        {JSON.stringify(entry.actions_executed, null, 2)}
                      </pre>
                  }
                </div>
              </div>
            )}

            {/* Form data */}
            {entry.form_data && Object.keys(entry.form_data).length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Form Data Submitted
                </h5>
                <div className="space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                  {Object.entries(entry.form_data).map(([key, val]) => (
                    <div key={key}>
                      <span className="font-medium">{key}:</span> {String(val)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function renderEvaluations(title: string, data: Record<string, unknown>) {
  const items: Array<Record<string, unknown>> = Array.isArray(data)
    ? data
    : Object.entries(data).map(([k, v]) => ({
        type: k,
        passed: typeof v === 'object' && v !== null ? (v as Record<string, unknown>).passed : v,
        reason: typeof v === 'object' && v !== null ? (v as Record<string, unknown>).reason : undefined,
      }));

  return (
    <div>
      <h5 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {title}
      </h5>
      <div className="space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className={`h-1 w-1 rounded-full ${item.passed ? 'bg-emerald-500' : 'bg-rose-500'}`} />
            <span>{String(item.type || item.guard_type || item.validator_type || 'check')}</span>
            {item.reason ? <span className="text-gray-400">({String(item.reason)})</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export default PlaybookTimelineEntry;
